/**
 * ServiceMark — a service's logo, coloured by health.
 *
 * Replaces a generic tick with something identifiable at a glance: you read
 * "HubSpot is fine" faster from a mark than from a label plus a tick. The mark
 * takes the STATE colour rather than the brand colour, so the row still scans
 * as green/amber/red — the logo says which service, the colour says how it is.
 *
 * Where no monochrome mark exists (DocuSeal, PropertyData, Ideal Postcodes),
 * a rounded-rect lettermark stands in. Deliberately a placeholder: swap in a
 * real mono SVG and the call site does not change.
 */
import React from "react";
import PropTypes from "prop-types";
import { SiHubspot, SiXero, SiStripe, SiResend } from "react-icons/si";
import ClozeSureMark from "./ClozeSureMark";
import "./ServiceMark.scss";

const STATE_COLOUR = {
  ok: "#10b981",
  pass: "#10b981",
  warn: "#f59e0b",
  inconclusive: "#f59e0b",
  breach: "#dc2626",
  fail: "#dc2626",
  unknown: "#9ca3af",
};

/** Matched on the metric key/label, so a new metric from the suite is picked
 *  up without a mapping table anyone has to remember to update. */
const MARKS = [
  { test: /hubspot/i, Icon: SiHubspot, name: "HubSpot" },
  { test: /xero/i, Icon: SiXero, name: "Xero" },
  { test: /stripe/i, Icon: SiStripe, name: "Stripe" },
  { test: /resend/i, Icon: SiResend, name: "Resend" },
  { test: /docuseal/i, letters: "DS", name: "DocuSeal" },
  { test: /propertydata/i, letters: "PD", name: "PropertyData" },
  { test: /ideal ?postcodes/i, letters: "IP", name: "Ideal Postcodes" },
  // Our OWN services get the ClozeSure mark rather than a lettermark — they
  // are not third parties and should not read as one.
  {
    test: /queue|backup|engagement|redirect|clozesure/i,
    Icon: ClozeSureMark,
    name: "ClozeSure",
  },
];

export function markFor(text) {
  return MARKS.find((m) => m.test.test(String(text || ""))) || null;
}

export default function ServiceMark({ text, state = "unknown", size = 18 }) {
  const mark = markFor(text);
  const colour = STATE_COLOUR[state] || STATE_COLOUR.unknown;

  if (!mark) return null;

  if (mark.Icon) {
    // `color` styles react-icons; `currentColor` covers our own mark. Setting
    // both means one call site works for either kind.
    return (
      <span
        className="service-mark"
        style={{ color: colour, display: "inline-flex" }}
      >
        <mark.Icon
          size={size}
          color={colour}
          title={mark.name}
          aria-label={mark.name}
        />
      </span>
    );
  }

  return (
    <span
      className="service-mark service-mark--letters"
      style={{ background: colour, width: size + 4, height: size + 4 }}
      title={mark.name}
      aria-label={mark.name}
    >
      {mark.letters}
    </span>
  );
}

ServiceMark.propTypes = {
  /** Any text identifying the service — a metric key or label. */
  text: PropTypes.string,
  state: PropTypes.string,
  size: PropTypes.number,
};
